import { ListItem, ListItemProps } from "@datamole/wds-component-list";
import clsx from "clsx";
import { type MouseEvent, type PropsWithoutRef, forwardRef, type Ref } from "react";

import { useMenuContext } from "../menu.hooks";

import styles from "./item.module.css";

type Props<TAsChild extends boolean = false> = PropsWithoutRef<ListItemProps<TAsChild>> & {
    closeOnClick?: boolean;
};

type RefProp<TAsChild extends boolean = false> = TAsChild extends true ? never : Ref<HTMLLIElement>;

const Item = <TAsChild extends boolean = false>(props: Props<TAsChild>, ref: RefProp<TAsChild>) => {
    const { className, asChild, children, onClick, closeOnClick = true, ...otherProps } = props;

    const { setIsOpen } = useMenuContext();

    const handleClick = (event: MouseEvent<HTMLLIElement>) => {
        if (onClick) {
            onClick(event);
        }

        if (closeOnClick) {
            setIsOpen(false);
        }
    };

    return (
        <ListItem
            {...otherProps}
            className={clsx(className, styles.item)}
            onClick={handleClick}
            asChild={asChild}
            ref={ref}
        >
            {children}
        </ListItem>
    );
};

// TODO: can be simplified by not disabling ref when `asChild` is true in ListItem
const ListItemWithForwardedRef = forwardRef(Item) as (<TAsChild extends boolean = false>(
    props: Props<TAsChild> & { ref?: RefProp<TAsChild> }
) => ReturnType<typeof Item>) & { displayName: string };

ListItemWithForwardedRef.displayName = "Menu.Content.Item";

export { ListItemWithForwardedRef as MenuContentItem };
export type { Props as MenuContentItemProps };
